<template>
  <div>
    <!-- トリガーボタン -->
    <button class="btn btn-outline-primary btn-sm w-100" @click="openModal">
      <i class="fas fa-cut"></i> クリップ
    </button>

    <!-- モーダルオーバーレイ -->
    <teleport to="body">
      <div v-if="isOpen" class="clip-modal-overlay" @click.self="closeModal">
        <div class="clip-modal-content" @click.stop>
          <button class="clip-modal-close" @click="closeModal">&times;</button>
          
          <div class="clip-modal-body">
            <!-- Video.js プレイヤー -->
            <div class="clip-player-container mb-3" ref="playerContainer"></div>

          <!-- クリップ作成フォーム -->
          <div class="clip-form-container">
            <div class="form-inline mb-3 justify-content-center">
              <div class="form-group mr-4">
                <label for="clip-from" class="mr-2">From</label>
                <div class="d-flex align-items-center">
                  <input
                    type="time"
                    id="clip-from"
                    class="form-control"
                    v-model="newClip.from"
                    :max="formattedMaxTime"
                    step="1"
                  />
                  <button type="button" class="btn btn-outline-secondary btn-sm ml-2" @click="setCurrentTime('from')">
                    {{ __('Set current position') }}
                  </button>
                </div>
              </div>
              <div class="form-group mr-4">
                <label for="clip-to" class="mr-2">To</label>
                <div class="d-flex align-items-center">
                  <input
                    type="time"
                    id="clip-to"
                    class="form-control"
                    v-model="newClip.to"
                    :max="formattedMaxTime"
                    step="1"
                  />
                  <button type="button" class="btn btn-outline-secondary btn-sm ml-2" @click="setCurrentTime('to')">
                    {{ __('Set current position') }}
                  </button>
                </div>
              </div>
              <button class="btn btn-primary ml-2" @click="addClip" :disabled="!canAddClip">
                クリップを追加
              </button>
            </div>

            <ul class="list-group mb-3" v-if="clips.items && clips.items.length > 0">
              <li v-for="(clip, index) in clips.items" :key="index" class="list-group-item">
                <div class="d-flex justify-content-between align-items-center">
                  <span>
                    <strong>ID</strong>:
                    <button
                      v-if="clip.transcoded"
                      type="button"
                      class="btn btn-link p-0"
                      @click="openClipPreview(clip.id)"
                    >
                      {{ clip.id }}
                    </button>
                    <span v-else>{{ clip.id }}</span>
                    - <strong>From</strong>: {{ clip.from }} - <strong>To</strong>: {{ clip.to }}
                    - <span v-if="clip.transcoded" class="badge badge-danger mr-1">{{ __('Ready') }}</span>
                    <span v-else class="badge badge-secondary mr-1">{{ __('Processing') }}</span>
                    - <strong>{{ __('clip.created_at') }}</strong>: {{ clip.created_at }}
                  </span>
                  <button v-if="isAdmin" class="btn btn-outline-danger btn-sm" @click="confirmRemoveClip(index)">
                    <i class="fas fa-trash-alt mr-1"></i> ゴミ箱
                  </button>
                </div>
              </li>
            </ul>

            <button class="btn btn-success btn-block" @click="submitClips" :disabled="!hasNewClips">
              クリップ作成
            </button>
          </div>
          </div>
        </div>
      </div>
    </teleport>

    <clip-show-modal
      ref="clipShowModal"
      :show-endpoint-template="showEndpointTemplate"
      :share-preview-fetch-template="sharePreviewFetchTemplate"
      :share-preview-add-template="sharePreviewAddTemplate"
      :share-download-fetch-template="shareDownloadFetchTemplate"
      :share-download-add-template="shareDownloadAddTemplate"
      :share-email-required="shareEmailRequired"
      :share-access-limit-required="shareAccessLimitRequired"
      :share-access-limit-max="shareAccessLimitMax"
      :share-expiry-required="shareExpiryRequired"
    ></clip-show-modal>
  </div>
</template>

<script>
import axios from "axios";
import videojs from "video.js";
import { computeCurrentTimecode } from "../lib/timecode";
import { registerTimecodeComponents, bindSeekBarTimecodeTooltip } from "../lib/videojsTimecodeControls";

export default {
  props: {
    videoDuration: { type: Number, required: true },
    videoId: { type: Number, required: true },
    videoSrc: { type: String, required: true },
    videoPoster: { type: String, default: "" },
    startTimecode: { type: String, default: null },
    frameRate: { type: [String, Number], default: null },
    endpoint: { type: String, required: true },
    endpointClips: { type: String, required: true },
    showEndpointTemplate: { type: String, default: "" },
    sharePreviewFetchTemplate: { type: String, default: "" },
    sharePreviewAddTemplate: { type: String, default: "" },
    shareDownloadFetchTemplate: { type: String, default: "" },
    shareDownloadAddTemplate: { type: String, default: "" },
    shareEmailRequired: { type: Boolean, default: true },
    shareAccessLimitRequired: { type: Boolean, default: true },
    shareAccessLimitMax: { type: Number, default: 10 },
    shareExpiryRequired: { type: Boolean, default: true },
    isAdmin: { type: Boolean, default: false },
  },
  data() {
    return {
      isOpen: false,
      player: null,
      timecodeToggleComponent: null,
      timecodeDisplayComponent: null,
      newClip: {
        from: "00:00:00",
        to: "00:00:00",
        new: true,
      },
      clips: { items: [] },
    };
  },
  mounted() {},
  computed: {
    formattedMaxTime() {
      return this.secondsToTime(this.videoDuration);
    },
    canAddClip() {
      const { from, to } = this.newClip;
      return from && to && from < to && this.timeToSeconds(to) <= this.videoDuration;
    },
    hasNewClips() {
      return this.clips.items.some((clip) => clip.new === true);
    },
    hasTimecode() {
      return Boolean(this.startTimecode && this.frameRate);
    },
  },
  methods: {
    openModal() {
      this.isOpen = true;
      document.body.style.overflow = "hidden";
      this.$nextTick(() => {
        this.initPlayer();
        this.fetchClips();
      });
    },
    resolveClipEndpoint(template, clipId) {
      if (!template) {
        return "";
      }
      return template.replace("__CLIP_ID__", clipId);
    },
    closeModal() {
      this.isOpen = false;
      document.body.style.overflow = "";
      this.resetNewClip();
      try {
        this.disposePlayer();
      } catch (error) {
        console.error("プレイヤー破棄エラー:", error);
      }
    },
    initPlayer() {
      if (this.player && typeof this.player.isDisposed === "function" && this.player.isDisposed()) {
        this.player = null;
      }
      const container = this.$refs.playerContainer;
      if (!container) {
        return;
      }
      if (this.player) {
        this.player.poster(this.videoPoster || "");
        this.player.src({
          src: this.videoSrc,
          type: this.determineVideoType(this.videoSrc),
        });
        this.player.load();
        this.bindTimecodeControls();
        return;
      }
      const videoElement = document.createElement("video");
      videoElement.className = "video-js vjs-big-play-centered vjs-fluid";
      container.innerHTML = "";
      container.appendChild(videoElement);
      this.player = videojs(videoElement, {
        autoplay: false,
        controls: true,
        preload: "auto",
        fluid: true,
        poster: this.videoPoster,
        playbackRates: [0.5, 1, 1.5, 2],
        controlBar: {
          pictureInPictureToggle: false,
          skipButtons: { forward: 10, backward: 10 },
          remainingTimeDisplay: false,
          currentTimeDisplay: true,
          timeDivider: true,
          durationDisplay: true,
          // video.js既定の children 順（playToggle 〜 fullscreenToggle）をベースに、
          // 経過時間/区切り/総時間の表示位置のみ remainingTimeDisplay と同じ
          // 「シークバーの右」に来るよう並び替える。他の子要素は既定を維持する。
          children: [
            'playToggle',
            'skipBackward',
            'skipForward',
            'volumePanel',
            'progressControl',
            'liveDisplay',
            'seekToLive',
            'currentTimeDisplay',
            'timeDivider',
            'durationDisplay',
            'customControlSpacer',
            'playbackRateMenuButton',
            'chaptersButton',
            'descriptionsButton',
            'subsCapsButton',
            'audioTrackButton',
            'pictureInPictureToggle',
            'fullscreenToggle'
          ]
        },
        userActions: {
          hotkeys(event) {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
            event.preventDefault()
            const duration = this.duration() || 0
            const current = this.currentTime() || 0
            const delta = event.key === 'ArrowRight' ? 10 : -10
            this.currentTime(Math.min(Math.max(current + delta, 0), duration))
          }
        },
        html5: {
          vhs: {
            withCredentials: true
          }
        },
        sources: [
          {
            src: this.videoSrc,
            type: this.determineVideoType(this.videoSrc),
          },
        ],
      });
      // クリックだけではフォーカスされないため、矢印キーシークをプレーヤー要素フォーカス時のみ発火させる
      this.player.el().setAttribute('tabindex', '0');
      registerTimecodeComponents(videojs);
      this.player.on('timeupdate', this.updateTimecodeDisplay);
      this.player.on('seeked', this.updateTimecodeDisplay);
      this.player.on('loadedmetadata', this.updateTimecodeDisplay);
      this.bindTimecodeControls();
    },
    updateTimecodeDisplay() {
      if (!this.timecodeDisplayComponent || !this.hasTimecode || !this.player) {
        return;
      }
      const currentSeconds = this.player.currentTime() || 0;
      const timecodeText = computeCurrentTimecode(this.startTimecode, this.frameRate, currentSeconds);
      this.timecodeDisplayComponent.setTimecodeText(timecodeText);
    },
    bindTimecodeControls() {
      if (!this.player) {
        return;
      }
      this.unbindTimecodeControls();
      if (!this.hasTimecode) {
        return;
      }
      const controlBar = this.player.getChild('ControlBar');
      if (!controlBar) {
        return;
      }
      const displayChildNames = controlBar.children().map((c) => c.name());
      const displayFsIndex = displayChildNames.indexOf('FullscreenToggle');
      const displayInsertIndex = displayFsIndex >= 0 ? displayFsIndex : displayChildNames.length;
      this.timecodeDisplayComponent = controlBar.addChild('TimecodeDisplay', {}, displayInsertIndex);

      const toggleChildNames = controlBar.children().map((c) => c.name());
      const toggleFsIndex = toggleChildNames.indexOf('FullscreenToggle');
      const toggleInsertIndex = toggleFsIndex >= 0 ? toggleFsIndex : toggleChildNames.length;
      this.timecodeToggleComponent = controlBar.addChild('TimecodeToggleButton', {}, toggleInsertIndex);
      this.updateTimecodeDisplay();

      bindSeekBarTimecodeTooltip(this.player, () => ({
        startTimecode: this.startTimecode,
        frameRate: this.frameRate,
        clipOffsetSeconds: 0
      }));
    },
    unbindTimecodeControls() {
      const controlBar = this.player ? this.player.getChild('ControlBar') : null;
      if (controlBar && this.timecodeToggleComponent) {
        controlBar.removeChild(this.timecodeToggleComponent);
        this.timecodeToggleComponent.dispose();
      }
      this.timecodeToggleComponent = null;
      if (controlBar && this.timecodeDisplayComponent) {
        controlBar.removeChild(this.timecodeDisplayComponent);
        this.timecodeDisplayComponent.dispose();
      }
      this.timecodeDisplayComponent = null;
      if (this.player) {
        this.player.removeClass('vjs-timecode-active');
      }
    },
    determineVideoType(src) {
      if (!src) return "";
      const lowerSrc = src.toLowerCase();
      if (lowerSrc.indexOf(".m3u8") !== -1) {
        return "application/x-mpegURL";
      }
      if (lowerSrc.indexOf(".mp4") !== -1) {
        return "video/mp4";
      }
      if (lowerSrc.indexOf(".mp3") !== -1 || lowerSrc.indexOf(".m4a") !== -1) {
        return "audio/mp3"; // video.js handles audio too
      }
      return "";
    },
    disposePlayer() {
      const container = this.$refs.playerContainer;
      this.timecodeToggleComponent = null;
      this.timecodeDisplayComponent = null;
      if (!this.player || (typeof this.player.isDisposed === "function" && this.player.isDisposed())) {
        this.player = null;
        if (container) {
          container.innerHTML = "";
        }
        return;
      }
      try {
        this.player.pause();
      } catch (error) {
        // no-op: pause failure should not block close
      }
      try {
        this.player.dispose();
      } catch (error) {
        // no-op: dispose failure should not block close
      } finally {
        this.player = null;
        if (container) {
          container.innerHTML = "";
        }
      }
    },
    async fetchClips() {
      try {
        const response = await axios.get(this.endpointClips, { params: { videoId: this.videoId } });
        const items = response?.data?.items ?? response?.data?.data?.items ?? [];
        this.clips = { items: Array.isArray(items) ? items : [] };
      } catch (error) {
        console.error("クリップ取得エラー:", error.response || error);
        this.clips = { items: [] };
      }
    },
    addClip() {
      this.clips.items.push({ ...this.newClip });
      this.resetNewClip();
    },
    resetNewClip() {
      this.newClip.from = "00:00:00";
      this.newClip.to = "00:00:00";
      this.newClip.new = true;
    },
    async confirmRemoveClip(index) {
      const targetClip = this.clips.items[index];
      let message = "ゴミ箱へ移動しますか？";
      if (targetClip.id) {
        message += `（ID: ${targetClip.id}）`;
      }
      if (confirm(message)) {
        if (targetClip.links && targetClip.links.remove) {
          try {
            await axios.get(targetClip.links.remove);
            this.clips.items.splice(index, 1);
          } catch (error) {
            console.error("削除エラー:", error);
            alert("削除に失敗しました");
          }
        } else {
          this.clips.items.splice(index, 1);
        }
      }
    },
    openClipPreview(clipId) {
      if (!clipId || !this.showEndpointTemplate || !this.$refs.clipShowModal) {
        return;
      }
      this.$refs.clipShowModal.open(clipId);
    },
    setCurrentTime(target) {
      if (!this.player) {
        return;
      }
      const currentSeconds = Math.floor(this.player.currentTime());
      if (!Number.isFinite(currentSeconds) || currentSeconds < 0) {
        return;
      }
      const formatted = this.secondsToTime(currentSeconds);
      if (target === "from") {
        this.newClip.from = formatted;
        return;
      }
      if (target === "to") {
        this.newClip.to = formatted;
      }
    },
    async submitClips() {
      const filteredClips = this.clips.items.filter((clip) => clip.new === true);
      try {
        await axios.post(this.endpoint, { videoId: this.videoId, clips: filteredClips });
        window.alert(this.__("Created a clip."));
        this.fetchClips();
      } catch (error) {
        const message = error?.response?.data?.message;
        if (message) {
          window.alert(message);
        } else {
          window.alert(this.__("Failed to create clip."));
        }
        console.error("送信エラー:", error.response || error);
      }
    },
    secondsToTime(seconds) {
      const hrs = Math.floor(seconds / 3600).toString().padStart(2, "0");
      const mins = Math.floor((seconds % 3600) / 60).toString().padStart(2, "0");
      const secs = (seconds % 60).toString().padStart(2, "0");
      return `${hrs}:${mins}:${secs}`;
    },
    timeToSeconds(time) {
      const [hrs, mins, secs] = time.split(":").map(Number);
      return hrs * 3600 + mins * 60 + secs;
    },
  },
  beforeUnmount() {
    this.disposePlayer();
  },
};
</script>

<style scoped>
.clip-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.clip-modal-content {
  position: relative;
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.clip-modal-close {
  position: absolute;
  top: 10px;
  right: 15px;
  font-size: 28px;
  font-weight: bold;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  z-index: 1;
}

.clip-modal-close:hover {
  color: #333;
}

.clip-modal-body {
  padding: 50px 30px 30px;
}

.clip-player-container {
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}

.clip-player-container .video-js {
  width: 100%;
  max-height: 400px;
}

.clip-form-container {
  padding-top: 0;
}
</style>
